import React, { useState, useRef } from 'react';
import Layout from '../components/Layout';
import { uploadTemplate, getProjects, getTemplates, updateProject, deleteTemplate } from '../api';
import VisualTemplateEditor from './VisualTemplateEditor';

function UploadModal({ onClose, onUploaded }) {
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [drag, setDrag] = useState(false);
  const fileRef = useRef();

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['svg', 'pdf', 'eps'].includes(ext)) {
      setError('Only SVG, PDF, or EPS files are supported');
      return;
    }
    setFile(f);
    setError('');
    if (!name) setName(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !name.trim()) return;
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      fd.append('file', file);
      const res = await uploadTemplate(fd);
      onUploaded(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Upload Template</h2>
          <p>SVG, PDF, or EPS — SVG is recommended (no Inkscape needed).</p>
        </div>
        {error && <div className="alert alert-error mb-4">{error}</div>}
        <form onSubmit={handleSubmit} className="stack stack-3">
          <div
            className={`upload-zone${drag ? ' drag-over' : ''}`}
            onClick={() => fileRef.current.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/></svg>
            {file ? (
              <p style={{ color: 'var(--accent)' }}>{file.name}</p>
            ) : (
              <>
                <p>Click or drag file here</p>
                <span>SVG · PDF · EPS</span>
              </>
            )}
            <input ref={fileRef} type="file" accept=".svg,.pdf,.eps" style={{ display: 'none' }} onChange={(e) => handleFile(e.target.files[0])} />
          </div>
          <div className="form-group">
            <label className="form-label">Template Name</label>
            <input className="form-input" placeholder="e.g. Standard ID Card" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !file || !name.trim()}>
              {loading ? <><span className="spinner" /> Uploading…</> : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AssignModal({ template, projects, onClose, onAssigned }) {
  const [projectId, setProjectId] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAssign = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      await updateProject(projectId, { template_id: template.id });
      onAssigned();
      onClose();
    } catch {
      alert('Failed to assign template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Assign Template</h2>
          <p>Assign "{template.name}" to a project.</p>
        </div>
        <div className="form-group">
          <label className="form-label">Select Project</label>
          <select className="form-select" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Choose project…</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAssign} disabled={!projectId || loading}>
            {loading ? <span className="spinner" /> : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [editorTarget, setEditorTarget] = useState(null);

  const loadTemplates = () => getTemplates().then((r) => setTemplates(r.data));

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    try {
      await deleteTemplate(templateId);
      setTemplates((prev) => prev.filter((t) => t.id !== templateId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  React.useEffect(() => {
    getProjects().then((r) => setProjects(r.data));
    loadTemplates();
  }, []);

  return (
    <Layout title="Templates">
      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onUploaded={(t) => setTemplates((prev) => [t, ...prev])}
        />
      )}
      {assignTarget && (
        <AssignModal
          template={assignTarget}
          projects={projects}
          onClose={() => setAssignTarget(null)}
          onAssigned={loadTemplates}
        />
      )}
      {editorTarget && (
        <VisualTemplateEditor
          template={editorTarget}
          onClose={() => setEditorTarget(null)}
          onSaved={(updated) => {
            setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
          }}
        />
      )}

      <div className="page-header">
        <div className="page-header-text">
          <h1>Templates</h1>
          <p>Upload and manage ID card design templates</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn btn-secondary" onClick={() => setEditorTarget({ name: 'Untitled Template', mapping_json: {}, isNew: true })}>
            + Create Blank Template
          </button>
          <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
            + Upload Template
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="alert alert-info">
          <strong>SVG Placeholders</strong> — In your SVG file, set element IDs to:
          {' '}<code style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem' }}>student_name</code>,{' '}
          <code style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem' }}>student_id</code>,{' '}
          <code style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem' }}>class_name</code>,{' '}
          <code style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem' }}>section</code>,{' '}
          <code style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem' }}>roll</code>,{' '}
          <code style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem' }}>student_photo</code>.
          The engine will fill them in automatically.
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <span style={{ fontSize: 32 }}>◧</span>
            <h3>No templates yet</h3>
            <p>Upload an SVG, PDF, or EPS ID card template to get started.</p>
            <button className="btn btn-primary" onClick={() => setShowUpload(true)}>Upload Template</button>
          </div>
        </div>
      ) : (
        <div className="grid-2">
          {templates.map((t) => (
            <div key={t.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text)' }}>{t.name}</div>
                  <div className="text-sm text-muted">{new Date(t.created_at).toLocaleDateString()}</div>
                </div>
                <span className="badge badge-green">ready</span>
              </div>
              {t.mapping_json && Object.keys(t.mapping_json).length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div className="form-label" style={{ marginBottom: 6 }}>Detected Placeholders</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {Object.entries(t.mapping_json).map(([k, info]) => (
                      <span key={k} className="badge badge-blue">
                        {k}{info.field_name ? ` → ${info.field_name}` : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-primary btn-sm" onClick={() => setEditorTarget(t)}>
                  Edit in Visual Editor
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setAssignTarget(t)}>
                  Assign to Project
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDeleteTemplate(t.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
